import React, { useEffect } from "react";
import PropTypes from "prop-types";
import _styles from "./EventSummaryDocument.module.css";
import styles from "./styles";
import {
  Document,
  Page,
  View,
  Text,
  Svg,
  Path,
  Link,
} from "@react-pdf/renderer";
import {
  Document as DisplayDoc,
  Page as DisplayPage,
} from "react-pdf/dist/entry.webpack";
import { v4 } from "uuid";
import { usePdf } from "../utils/usePdf";

/**
 * View component. Document with details about the event
 * (Event Center Information,
 * Venue Security and Emergency Documents,
 * Key Location Information (Emergency Locations),
 * Documents,
 * Special Circumstances,
 * Notes, Staff (Key Contacts, Security Team, Event Staff),
 * Nearby Incidents).
 */
export function EventSummaryDocument({
  centerInfo,
  event,
  emergencyDocs,
  keyLocations,
  documents,
  specialCircumstances,
  keyContacts,
  securityTeam,
  eventStaff,
  nearbyIncidents,
  onLoad,
}) {
  // Component for rendered base PDF file.
  const FormattedDoc = () => (
    <Document>
      <Page style={styles.page} size="LETTER">
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>{event.name}</Text>
          <View style={styles.flex}>
            {[
              { header: "Event Center", info: event.center },
              { header: "City", info: event.city },
              { header: "State", info: event.state },
              { header: "Event Date", info: event.date },
              { header: "Complaints", info: event.complaints },
            ].map(({ header, info }) =>
              info ? (
                <View style={styles["column-flex"]}>
                  <Text style={[styles.bold, styles["item-header"]]}>
                    {header}
                  </Text>
                  <Text style={styles["info-text"]}>{info}</Text>
                </View>
              ) : null
            )}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>
            Event Center Information
          </Text>
          <View style={[styles.flex, styles["event-center-flex"]]}>
            {[
              { header: "Address Line 1", info: centerInfo.address1 },
              { header: "Address Line 2", info: centerInfo.address2 },
              { header: null, info: null },
              {
                header: "Emergency Contact",
                info: centerInfo.emergencyContact,
              },
              { header: "Phone Number", info: centerInfo.phoneNumber },
              { header: "Email", info: centerInfo.email },
            ].map(({ header, info }, index) => (
              <View
                style={[
                  styles["column-flex"],
                  styles["event-center-item"],
                  [0, 1, 2].includes(index)
                    ? styles["event-center-margin"]
                    : null,
                ]}
              >
                <Text style={[styles.bold, styles["event-center-header"]]}>
                  {header}
                </Text>
                <Text style={styles["info-text"]}>{info}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>Key Contacts</Text>
          <View style={styles["column-flex"]}>
            {[
              {
                position: "Position",
                name: "Name",
                number: "Phone Number",
                email: "Email",
              },
              ...keyContacts,
            ].map((contact, index) => (
              <View
                style={[
                  styles.flex,
                  index % 2 === 0 ? styles["grey-row"] : null,
                  index === 0 ? styles["table-header"] : null,
                ]}
                wrap={false}
              >
                <Text style={styles.cell}>{contact.position}</Text>
                <Text style={styles.cell}>{contact.name}</Text>
                <Text style={styles.cell}>{contact.number}</Text>
                <Text style={styles.cell}>{contact.email}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>Key Locations</Text>
          <View style={styles["column-flex"]}>
            {keyLocations.map((location) => (
              <View style={styles.flex} wrap={false}>
                <View style={[styles.cell, styles.flex, { paddingLeft: 0 }]}>
                  <Svg width="12" height="12">
                    <Path
                      d="M 10.41866397857666 0.08256390690803528 L 0.6738936901092529 4.580186367034912 C -0.4503947198390961 5.104948043823242 -0.07563192397356033 6.753998279571533 1.123655796051025 6.753998279571533 L 5.246514797210693 6.753998279571533 L 5.246514797210693 10.87685680389404 C 5.246514797210693 12.07614421844482 6.895565032958984 12.45114231109619 7.420327186584473 11.32661914825439 L 11.91795063018799 1.581848502159119 C 12.2927131652832 0.6820897459983826 11.31818866729736 -0.2924340665340424 10.41866493225098 0.08256309479475021 Z"
                      strokeWidth={1}
                      fill="rgb(244, 197, 121)"
                    />
                  </Svg>
                  <Link
                    src={`https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
                      location.address2
                        ? location.address1 + ", " + location.address2
                        : location.address1
                    )}`}
                    style={styles.location}
                  >
                    {location.name}
                  </Link>
                </View>
                <Text style={styles.cell}>{location.address1}</Text>
                <Text style={styles.cell}>{location.address2}</Text>
                <Text style={styles.cell}>{location.phoneNumber}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>
            Special Circumstances
          </Text>
          <Text style={styles.circumstances}>{specialCircumstances}</Text>
        </View>
        {[securityTeam, eventStaff].map((list, index) => (
          <View style={styles.section}>
            <Text style={[styles.header, styles.bold]}>
              {index === 0 ? "Security Team" : "Event Staff"}
            </Text>
            <View style={styles["column-flex"]}>
              <View
                style={[
                  styles["grey-row"],
                  styles["table-header"],
                  styles.flex,
                ]}
              >
                <View style={[styles.cell, styles.pin]}>
                  <Svg width="7.55" height="13.52">
                    <Path
                      d="M 1.446101307868958 -0.005703814793378115 C 1.148093938827515 0.06892064958810806 0.9451644420623779 0.5075100064277649 0.9928455948829651 0.9739128947257996 C 1.040526747703552 1.4403156042099 1.320762634277344 1.757914900779724 1.618769884109497 1.683290600776672 L 2.158360004425049 1.683290600776672 L 2.158360004425049 5.061279296875 L 1.079180002212524 5.061279296875 C 0.4856310486793518 5.061279296875 0 5.821326732635498 0 6.750273704528809 L 3.237540006637573 6.750273704528809 L 3.237540006637573 11.81725692749023 L 3.712379217147827 13.50625133514404 L 4.316720008850098 11.81725692749023 L 4.316720008850098 6.750273704528809 L 7.55426025390625 6.750273704528809 C 7.55426025390625 5.821326732635498 7.068629264831543 5.061279296875 6.475080013275146 5.061279296875 L 5.395900249481201 5.061279296875 L 5.395900249481201 1.683290600776672 L 5.935490131378174 1.683290600776672 C 6.233497619628906 1.683290600776672 6.475080013275146 1.305196285247803 6.475080013275146 0.8387933969497681 C 6.475080013275146 0.3723904490470886 6.233497619628906 -0.005703814793378115 5.935490131378174 -0.005703814793378115 L 1.618770003318787 -0.005703814793378115 C 1.586460471153259 -0.01027333550155163 1.553953409194946 -0.01027333550155163 1.521643757820129 -0.005703814793378115 C 1.500079751014709 -0.007732465863227844 1.47845721244812 -0.007732465863227844 1.456893086433411 -0.005703814793378115 Z"
                      stroke="rgb(0, 0, 0)"
                      strokeWidth={1}
                      fill="rgb(0, 0, 0)"
                    />
                  </Svg>
                </View>
                <Text style={[styles.cell, styles["list-cell"]]}>Name</Text>
                <Text style={[styles.cell, styles["list-cell"]]}>Phone</Text>
                <Text style={[styles.cell, styles["list-cell"]]}>Email</Text>
                <Text style={[styles.cell, styles["list-cell"]]}>CCW</Text>
              </View>
              {list
                .sort((a, b) => b.pinned - a.pinned)
                .map((item) => (
                  <View style={styles.flex} wrap={false}>
                    <View style={[styles.cell, styles.pin]}>
                      {item.pinned ? (
                        <Svg
                          width="7.55"
                          height="13.52"
                          style={{ transform: `scale(${4.78 / 7.55})` }}
                        >
                          <Path
                            d="M 1.446101307868958 -0.005703814793378115 C 1.148093938827515 0.06892064958810806 0.9451644420623779 0.5075100064277649 0.9928455948829651 0.9739128947257996 C 1.040526747703552 1.4403156042099 1.320762634277344 1.757914900779724 1.618769884109497 1.683290600776672 L 2.158360004425049 1.683290600776672 L 2.158360004425049 5.061279296875 L 1.079180002212524 5.061279296875 C 0.4856310486793518 5.061279296875 0 5.821326732635498 0 6.750273704528809 L 3.237540006637573 6.750273704528809 L 3.237540006637573 11.81725692749023 L 3.712379217147827 13.50625133514404 L 4.316720008850098 11.81725692749023 L 4.316720008850098 6.750273704528809 L 7.55426025390625 6.750273704528809 C 7.55426025390625 5.821326732635498 7.068629264831543 5.061279296875 6.475080013275146 5.061279296875 L 5.395900249481201 5.061279296875 L 5.395900249481201 1.683290600776672 L 5.935490131378174 1.683290600776672 C 6.233497619628906 1.683290600776672 6.475080013275146 1.305196285247803 6.475080013275146 0.8387933969497681 C 6.475080013275146 0.3723904490470886 6.233497619628906 -0.005703814793378115 5.935490131378174 -0.005703814793378115 L 1.618770003318787 -0.005703814793378115 C 1.586460471153259 -0.01027333550155163 1.553953409194946 -0.01027333550155163 1.521643757820129 -0.005703814793378115 C 1.500079751014709 -0.007732465863227844 1.47845721244812 -0.007732465863227844 1.456893086433411 -0.005703814793378115 Z"
                            stroke="rgb(0, 0, 0)"
                            strokeWidth={1}
                            fill="rgb(0, 0, 0)"
                          />
                        </Svg>
                      ) : (
                        <View style={styles.unchecked} />
                      )}
                    </View>
                    <Text
                      style={[
                        styles.location,
                        styles.cell,
                        styles["list-cell"],
                      ]}
                    >
                      {item.name}
                    </Text>
                    <Text style={[styles.cell, styles["list-cell"]]}>
                      {item.phoneNumber}
                    </Text>
                    <Text style={[styles.cell, styles["list-cell"]]}>
                      {item.email}
                    </Text>
                    <Text style={[styles.cell, styles["list-cell"]]}>
                      {item.CCW ? "Yes" : "No"}
                    </Text>
                  </View>
                ))}
            </View>
          </View>
        ))}
      </Page>
      <Page style={[styles.page, styles.landscape]} orientation="landscape">
        <View style={styles.section}>
          <Text style={[styles.header, styles.bold]}>Nearby Incidents</Text>
          <View style={[styles["column-flex"], styles.bold]}>
            {[
              {
                number: "Incident #",
                status: "Status",
                type: "Type",
                severity: "Severity",
                iboNum: "IBO #",
                caseOwner: "Case Owner",
                dateCreated: "Date Created",
              },
              ...nearbyIncidents,
            ].map((incident, index) => (
              <View
                style={[
                  styles.flex,
                  index % 2 === 0 ? styles["grey-row"] : null,
                  index === 0 ? styles["table-header"] : null,
                  // { opacity: "1" },
                ]}
                wrap={false}
              >
                {Object.values(incident).map((value, _index) => (
                  <Text
                    style={[
                      styles.incident,
                      index !== 0 ? styles.light : null,
                      (_index === 0 || _index === 4) && index !== 0
                        ? styles.number
                        : null,
                      _index === 6 || _index === 3 ? styles.shrink : null,
                    ]}
                  >
                    {value}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );

  let _documents = [];
  let sections = ["Event Details"];

  useEffect(() => {
    [...emergencyDocs, ...documents].forEach((doc) => {
      _documents.push(doc.src);
      sections.push(doc.name);
    });
  }, []);

  // Combined PDF with its data.
  const { document, documentObject } = usePdf(
    FormattedDoc(),
    _documents,
    "Event Summary Document"
  );

  // Fire onLoad when documentObject is returned from hook.
  useEffect(() => {
    if (documentObject) {
      onLoad(documentObject);
    }
  }, [documentObject, onLoad]);

  return (
    <div className={_styles.container}>
      {document.file && documentObject && (
        <>
          <a
            href={document.file}
            download="EventSummaryDocument"
            hidden
            disabled
          >
            Download
          </a>
          <DisplayDoc file={document.file}>
            {document.pages.map((page) => (
              <DisplayPage pageNumber={page + 1} key={v4()} />
            ))}
          </DisplayDoc>
        </>
      )}
    </div>
  );
}

EventSummaryDocument.propTypes = {
  /**
   * Object with the event center details.
   */
  centerInfo: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    emergencyContact: PropTypes.string,
  }),
  /**
   * Object with the event's details.
   */
  event: PropTypes.shape({
    name: PropTypes.string,
    center: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    date: PropTypes.string,
    complaints: PropTypes.number,
  }),
  /**
   * Array of emergency document objects.
   */
  emergencyDocs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      src: PropTypes.string,
    })
  ),
  /**
   * Array of key locations.
   */
  keyLocations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      phoneNumber: PropTypes.string,
    })
  ),
  /**
   * Array of document objects.
   */
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      src: PropTypes.string,
    })
  ),
  /**
   * String to display the special circumstances.
   */
  specialCircumstances: PropTypes.string,
  /**
   * Array of key contact objects.
   */
  keyContacts: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  /**
   * Array of security member objects.
   */
  securityTeam: PropTypes.arrayOf(
    PropTypes.shape({
      pinned: PropTypes.bool,
      name: PropTypes.string,
      phoneNumber: PropTypes.string,
      email: PropTypes.string,
      CCW: PropTypes.bool,
    })
  ),
  /**
   * Array of staff member objects.
   */
  eventStaff: PropTypes.arrayOf(
    PropTypes.shape({
      pinned: PropTypes.bool,
      name: PropTypes.string,
      phoneNumber: PropTypes.string,
      email: PropTypes.string,
      CCW: PropTypes.bool,
    })
  ),
  /**
   * Array of incident objects.
   */
  nearbyIncidents: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
      status: PropTypes.string,
      type: PropTypes.string,
      severity: PropTypes.string,
      iboNum: PropTypes.string,
      caseOwner: PropTypes.string,
      dateCreated: PropTypes.string,
    })
  ),
  /**
   * Function that fires when the generated document is loaded.
   * Takes an object with a Blob, ObjectURL, and the Uint8Array as the parameter.
   */
  onLoad: PropTypes.func,
};
